import {
    faBan,
    faBinoculars,
    faCheck,
    faClock,
    faDatabase,
    faEdit,
    faEnvelope,
    faEye,
    faEyeSlash,
    faFileImport,
    faFileInvoice,
    faFileSignature,
    faHourglassEnd,
    faNewspaper,
    faTable,
    faTimes,
    faTrashAlt,
    faUndo,
    faUser
} from '@fortawesome/free-solid-svg-icons';
import { Action, Column, DataTable, DataTableContainer, Filter, TableActionButton } from 'components/DataTable';
import { ActionCallbackProps } from 'components/DataTable/DataTable';
import { Booking } from 'components/EventCalendar/types';
import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../../../hooks/useApi';
import FormDataPreview from '../../../Formular/FormDataPreview';
import BookingRequestResponseForm from '../BookingRequestResponseForm';
import { CancellationForm } from '../CancellationForm';
import { ManualBookingForm } from '../ManualBookingForm';
import { DocumentImport } from '../DocumentImport';
import { BookingMetaComponent } from './BookingMetaComponent';
import { DatesComponent } from './DatesComponent';
import { PaymentComponent } from './PaymentComponent';
import { StatusComponent } from './StatusComponent';
import { UserComponent } from './UserComponent';

import useModalManager from '@hooks/useModalManager';
import { Button } from '@ui';
import { useClubSlug } from 'components/Clubs/hooks';
import useFileDownload from 'hooks/useFileDownload';
import { head } from 'lodash';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useConfirmedCallback } from '../../../../hooks/useConfirmedCallback';
import { BookingServiceHoursAssistant } from '../../../ClubMemberManagement/BookingServiceHoursAssistant';
import { BookingToClubMemberAssistant } from '../../../ClubMemberManagement/BookingToClubMemberAssistant';
import useFetchFields from '../../../Formular/hooks/useFetchFields';
import { UserInvoice } from '../../../UserInvoice/types';
import BookingImportAssistant from '../BookingImportAssistant';
import MessageComponent from '../MessageComponent';
import { UpdatePriceForm } from '../UpdatePriceForm';
import { useExperimental } from 'hooks/useExperimental';

const DeleteBooking = dynamic(() => import('../DeleteBooking'));
const EditBookingForm = dynamic(() => import('../EditBookingForm'));
const GenerateBookingInvoices = dynamic(() => import('../GenerateBookingInvoices'));
const MultiSubscribeToNewsletter = dynamic(() => import('../../../Newsletter/MultiSubscribeToNewsletter'));
const SepaMandateFromBookingAssistant = dynamic(
    () => import('components/UserInvoice/SepaMandateFromBooking/SepaMandateFromBookingAssistant')
);

interface Props {
    eventId: number;
    formId?: number;
    archive?: boolean;
}

/** Renders the paginated booking list for an event, with bulk actions and per-row actions. */
export const BookingList = ({ eventId, formId, archive = false }: Props) => {
    const { t } = useTranslation('bookings');
    const sepaExperimental = useExperimental().includes('sepa_v1');
    const clubSlug = useClubSlug();
    const asyncList = useRef<any>(undefined);
    const api = useApi();
    const { addModal, removeModal } = useModalManager();
    const [isLoading, setIsLoading] = useState(false);
    const download = useFileDownload();

    /** Opens a modal to subscribe the selected/filtered bookers to a newsletter. */
    const showNewsletterForm = ({ selection, filter }: ActionCallbackProps) =>
        addModal({
            title: t('booking.list.modals.addNewsletter.title'),
            content: (id) => (
                <MultiSubscribeToNewsletter
                    clubSlug={clubSlug}
                    filter={filter}
                    eventId={eventId}
                    selection={selection}
                    variant="bookers"
                    callback={() => {
                        removeModal(id);
                        asyncList.current?.reload();
                    }}
                />
            ),
            show: true,
            size: 50
        });

    /** Opens the enrollment import assistant modal. */
    const handleImportEnrollmentAssistant = () =>
        addModal({
            title: t('booking.list.modals.import.title'),
            content: (id) => (
                <BookingImportAssistant
                    formId={formId}
                    eventId={eventId}
                    cb={() => {
                        asyncList.current?.reload();
                        removeModal(id);
                    }}
                />
            ),
            size: 70,
            show: true
        });

    /** Opens the modal to change a booking's price. */
    const handleShowUpdatePriceForm = (item: Booking) =>
        addModal({
            title: t('booking.list.modals.updatePrice.title'),
            content: (id) => (
                <UpdatePriceForm
                    booking={item}
                    callback={(data) => {
                        if (data) asyncList.current?.update(item.id, data);
                        else asyncList.current?.reload();
                        removeModal(id);
                    }}
                />
            ),
            show: true
        });

    /** Opens the modal to cancel a booking. */
    const handleShowCancellationForm = (item: Booking) =>
        addModal({
            title: t('booking.list.modals.cancellation.title'),
            content: (id) => (
                <CancellationForm
                    booking={item}
                    callback={() => {
                        asyncList.current?.reload();
                        removeModal(id);
                    }}
                />
            ),
            show: true
        });

    /** Opens the modal to accept or decline a booking request. */
    const handleShowRequestResponseForm = (item: Booking, action: 'accept' | 'decline') =>
        addModal({
            title:
                action === 'accept'
                    ? t('booking.list.modals.acceptRequest', { id: item.id })
                    : t('booking.list.modals.declineRequest', { id: item.id }),
            content: (id) => (
                <BookingRequestResponseForm
                    action={action}
                    booking={item}
                    withPayment={item.price > 0 && !!item.event?.online_payments}
                    callback={() => {
                        asyncList.current?.reload();
                        removeModal(id);
                    }}
                />
            ),
            show: true
        });

    /** Requests a CSV export for the selected/filtered bookings and triggers the download. */
    const handleDownloadCSV = ({
        selection,
        filter,
        withFormData = false
    }: ActionCallbackProps & { withFormData?: boolean }) => {
        setIsLoading(true);
        void api({
            url: `booking/${eventId}/csv`,
            method: 'POST',
            data: { selection, filter, withFormData },
            onBefore: () => setIsLoading(false),
            onSuccess: (data: { download_id: React.Key }) => download(data.download_id)
        });
    };

    /** Requests a PDF export of the form data for the selected/filtered bookings and triggers the download. */
    const handleDownloadFormDataPdf = ({ selection, filter }: ActionCallbackProps) => {
        setIsLoading(true);
        void api({
            url: `booking/${eventId}/form-data/pdf`,
            method: 'POST',
            data: { selection, filter },
            onBefore: () => setIsLoading(false),
            onSuccess: (data: { download_id: React.Key }) => download(data.download_id)
        });
    };

    /** Opens the modal to create a manual booking. */
    const handleShowManualBookingForm = () =>
        addModal({
            title: t('booking.list.modals.manualBooking.title'),
            content: (id) => (
                <ManualBookingForm
                    callback={() => {
                        asyncList.current.reload();
                        removeModal(id);
                    }}
                    eventId={eventId}
                    clubSlug={clubSlug}
                />
            ),
            show: true,
            size: 70
        });

    /** Opens the AI import: upload registration forms, check the values, book them. */
    const handleShowDocumentImport = () =>
        addModal({
            title: t('booking.list.modals.documentImport.title'),
            content: () => <DocumentImport eventId={eventId} onBooked={() => asyncList.current?.reload()} />,
            show: true,
            size: 100,
            onClose: () => asyncList.current?.reload()
        });

    /** Opens the modal to write a message to the selected/filtered bookers. */
    const handleShowMessageComponent = ({ filter, selection }: ActionCallbackProps) => {
        addModal({
            title: t('booking.list.modals.message.title'),
            content: (id) => (
                <MessageComponent
                    callback={() => {
                        asyncList.current.reload();
                        removeModal(id);
                    }}
                    eventId={eventId}
                    bookings={selection}
                    filter={filter}
                />
            ),
            show: true,
            size: 70
        });
    };

    /** Flips a booking's read status. The server answers with the updated record, so the row is
     * patched in place instead of reloading the whole page. */
    const handleToggleRead = (item: Booking) => {
        void api({
            url: `booking/${item.id}/read`,
            method: 'PUT',
            data: { read: !item.read_at },
            onSuccess: (data: { data: Booking }) => asyncList.current?.update(item.id, data.data)
        });
    };

    /** Marks the selected/filtered bookings as read or unread. */
    const handleReadStatusMultiple = ({ selection, filter }: ActionCallbackProps, read: boolean) => {
        setIsLoading(true);
        void api({
            url: `booking/${eventId}/read-status`,
            method: 'PUT',
            data: { selection, filter, read },
            onBefore: () => setIsLoading(false),
            onSuccess: () => asyncList.current?.reload()
        });
    };

    /** Moves a waiting-list booking up to a confirmed spot. */
    const handleMoveUp = (item: Booking) => {
        void api({
            url: `booking/${item.id}/move-up`,
            method: 'PUT',
            onSuccess: () => asyncList.current?.reload()
        });
    };

    /** Revokes a booking's cancellation after user confirmation. */
    const handleRevokeCancellation = useConfirmedCallback((item: Booking) => {
        void api({
            url: `booking/${item.id}/revoke`,
            method: 'PUT',
            onSuccess: () => asyncList.current?.reload()
        });
    });

    /** Opens the modal to edit a booking's form data. */
    const handleShowEditBooking = (formDataId: number) => {
        addModal({
            title: t('booking.list.modals.editBooking.title'),
            content: (id) => <EditBookingForm cb={() => removeModal(id)} formDataId={formDataId} lenient />,
            show: true
        });
    };

    /** Opens the modal to delete a booking. */
    const handleShowDeleteBooking = (item: Booking) => {
        addModal({
            title: t('booking.list.modals.deleteBooking.title'),
            content: (id) => {
                return (
                    <DeleteBooking
                        booking={item}
                        callback={() => {
                            asyncList.current?.remove(item.id);
                            removeModal(id);
                        }}
                    />
                );
            },
            show: true
        });
    };

    /** Opens a preview of a booking's submitted form data. */
    const handleShowFormData = (item: Booking) =>
        addModal({
            title: t('booking.list.modals.formData.title'),
            content: <FormDataPreview formDataId={item.form_data_id} />,
            show: true,
            size: 70
        });

    /** Opens the modal to generate invoices for the selected/filtered bookings. */
    const handleGenerateInvoiceMultiple = ({ filter, selection }: ActionCallbackProps) => {
        addModal({
            title: t('booking.list.modals.generateInvoices.title'),
            content: (id) => (
                <GenerateBookingInvoices
                    eventId={eventId}
                    filter={filter}
                    selection={selection}
                    callback={() => removeModal(id)}
                />
            ),
            show: true,
            size: 70
        });
    };

    const columns: Column<Booking>[] = [
        {
            uid: 'user_name',
            name: t('booking.list.columns.user'),
            width: '22%',
            renderer(_, item?) {
                return <UserComponent booking={item} />;
            }
        },
        {
            uid: 'start',
            name: t('booking.list.columns.dates'),
            width: '11%',
            renderer(_, item?) {
                return <DatesComponent booking={item} showCancellation={false} />;
            }
        },
        {
            uid: 'created_at',
            name: t('booking.list.columns.createdAt'),
            allowsSorting: true,
            width: '8%',
            renderer(_, item?) {
                return <BookingMetaComponent booking={item} />;
            }
        },
        {
            uid: 'cancelled_at',
            name: t('booking.list.columns.status'),
            width: '13%',
            renderer(_, item?) {
                return <StatusComponent booking={item} />;
            }
        },
        {
            uid: 'price',
            name: t('booking.list.columns.price'),
            allowsSorting: true,
            align: 'end',
            width: '10%',
            renderer(_, item?) {
                return <PaymentComponent booking={item} onEditPrice={handleShowUpdatePriceForm} />;
            }
        },
        {
            uid: 'actions',
            name: t('booking.list.columns.actions'),
            align: 'end',
            width: '36%'
        }
    ];

    const { filters: formFilters } = useFetchFields(formId);
    const filters: Filter[] = [
        {
            filterName: t('booking.list.filters.ids.label'),
            filterKey: 'ids',
            filterPlaceholder: t('booking.list.filters.ids.placeholder'),
            filterType: 'text'
        },
        { filterName: t('booking.list.filters.cancelledAt'), filterKey: 'cancelled_at', filterType: 'date' },
        { filterName: t('booking.list.filters.createdAt'), filterKey: 'created_at', filterType: 'date' },
        {
            filterName: t('booking.list.filters.lastInvoiceCreatedAt'),
            filterKey: 'last_invoice_created_at',
            filterType: 'date'
        },
        {
            filterName: t('booking.list.filters.invoiceExisting.label'),
            filterKey: 'invoice_existing',
            filterType: 'select',
            options: [
                { label: t('booking.common.yes'), value: String(1) },
                { label: t('booking.common.no'), value: String(0) }
            ]
        },
        {
            filterName: t('booking.list.filters.isMember.label'),
            filterKey: 'is_member',
            filterType: 'select',
            options: [
                { label: t('booking.list.filters.isMember.member'), value: String(1) },
                { label: t('booking.list.filters.isMember.nonMember'), value: String(0) }
            ]
        },
        {
            filterName: t('booking.list.filters.importedIntoTrainingplan.label'),
            filterKey: 'imported_into_trainingplan',
            filterType: 'select',
            options: [
                { label: t('booking.common.yes'), value: String(1) },
                { label: t('booking.common.no'), value: String(0) }
            ]
        },
        { filterName: t('booking.list.filters.price'), filterKey: 'price', filterType: 'numeric' },
        {
            filterName: t('booking.list.filters.cancellationReason'),
            filterKey: 'cancellation_reason',
            filterType: 'text'
        },
        {
            filterName: t('booking.list.filters.isWaiting.label'),
            filterKey: 'is_waiting',
            filterType: 'select',
            options: [
                { label: t('booking.common.yes'), value: String(1) },
                { label: t('booking.common.no'), value: String(0) }
            ]
        },
        {
            filterName: t('booking.list.filters.approvalStatus.label'),
            filterKey: 'approval_status',
            filterType: 'select',
            options: [
                { label: t('booking.list.filters.approvalStatus.pending'), value: 'pending' },
                { label: t('booking.list.filters.approvalStatus.approved'), value: 'approved' },
                { label: t('booking.list.filters.approvalStatus.declined'), value: 'declined' },
                { label: t('booking.list.filters.approvalStatus.none'), value: 'none' }
            ]
        },
        {
            filterName: t('booking.list.filters.cancelled.label'),
            filterKey: 'cancelled',
            filterType: 'select',
            options: [
                { label: t('booking.common.yes'), value: String(1) },
                { label: t('booking.common.no'), value: String(0) }
            ]
        },
        {
            filterName: t('booking.list.filters.read.label'),
            filterKey: 'read',
            filterType: 'select',
            options: [
                { label: t('booking.common.yes'), value: String(1) },
                { label: t('booking.common.no'), value: String(0) }
            ]
        },
        ...formFilters
    ];

    const actions: Action[] = [
        {
            key: 'import',
            label: t('booking.list.actions.import'),
            icon: faFileImport,
            action: () => handleImportEnrollmentAssistant()
        },
        {
            key: 'csv',
            label: t('booking.list.actions.csv'),
            icon: faTable,
            action: (selection) => handleDownloadCSV(selection)
        },
        formId && {
            key: 'csv_with_form_data',
            label: t('booking.list.actions.csvWithFormData'),
            icon: faTable,
            action: (selection) => handleDownloadCSV({ ...selection, withFormData: true })
        },
        formId && {
            key: 'form_data_pdf',
            label: t('booking.list.actions.formDataPdf'),
            icon: faFileInvoice,
            action: (selection) => handleDownloadFormDataPdf(selection)
        },
        {
            key: 'invoice',
            label: t('booking.list.actions.invoice'),
            icon: faFileInvoice,
            action: (selection) => handleGenerateInvoiceMultiple({ ...selection })
        },
        {
            key: 'message',
            label: t('booking.list.actions.message'),
            icon: faEnvelope,
            action: (selection) => handleShowMessageComponent({ ...selection })
        },
        {
            key: 'add-newsletter',
            label: t('booking.list.actions.addNewsletter'),
            icon: faNewspaper,
            action: showNewsletterForm
        },
        {
            key: 'create-members',
            label: t('booking.list.actions.createMembers'),
            icon: faUser,
            action: ({ selection, filter }) =>
                handleShowClubMemberAssistant(
                    selection === 'all' ? null : Array.from(selection ?? []).map(Number),
                    filter
                )
        },
        // Noch experimentell; das Flag steuert nur die Sichtbarkeit, nicht den Zugriff.
        ...(sepaExperimental
            ? ([
                  {
                      key: 'create-sepa-mandates',
                      label: t('booking.list.actions.createSepaMandates'),
                      icon: faFileSignature,
                      action: ({ selection, filter }: ActionCallbackProps) =>
                          handleShowSepaMandateAssistant(
                              selection === 'all' ? null : Array.from(selection ?? []).map(Number),
                              filter
                          )
                  }
              ] as Action[])
            : []),
        {
            key: 'credit-service-hours',
            label: t('booking.list.actions.creditServiceHours'),
            icon: faClock,
            action: ({ selection, filter }) =>
                handleShowServiceHoursAssistant(
                    selection === 'all' ? null : Array.from(selection ?? []).map(Number),
                    filter
                )
        },
        // Only null is filtered out downstream, so a `false` would reach the dropdown and blow up
        // on action.key — spread instead of using &&.
        ...(archive
            ? []
            : ([
                  {
                      key: 'mark-read',
                      label: t('booking.list.actions.markRead'),
                      icon: faEye,
                      action: (selection) => handleReadStatusMultiple(selection, true)
                  },
                  {
                      key: 'mark-unread',
                      label: t('booking.list.actions.markUnread'),
                      icon: faEyeSlash,
                      action: (selection) => handleReadStatusMultiple(selection, false)
                  }
              ] as Action[]))
    ];

    /** Opens the assistant for the given bookings; null means the whole selection ("all"), in which case the
     * table filter (if any) is forwarded so "select all" respects what the user filtered down to. */
    const handleShowClubMemberAssistant = (bookingIds: number[] | null, filter?: ActionCallbackProps['filter']) => {
        addModal({
            title: t('booking.list.modals.createMember.title'),
            content: (id) => (
                <BookingToClubMemberAssistant
                    eventId={eventId}
                    formId={formId}
                    bookingIds={bookingIds}
                    filter={filter}
                    callback={() => {
                        removeModal(id);
                        asyncList.current?.reload();
                    }}
                />
            ),
            size: 70,
            show: true
        });
    };

    /** Opens the mandate assistant for the given bookings; null means the whole selection ("all"),
     * in which case the table filter is forwarded so "select all" respects what the user filtered down to. */
    const handleShowSepaMandateAssistant = (bookingIds: number[] | null, filter?: ActionCallbackProps['filter']) => {
        addModal({
            title: t('booking.list.modals.createSepaMandates.title'),
            content: (id) => (
                <SepaMandateFromBookingAssistant
                    eventId={eventId}
                    formId={formId}
                    bookingIds={bookingIds}
                    filter={filter}
                    callback={() => {
                        removeModal(id);
                        asyncList.current?.reload();
                    }}
                />
            ),
            size: 70,
            show: true
        });
    };

    /** Opens the service hours assistant for the given bookings; null means the whole selection ("all"),
     * in which case the table filter is forwarded so "select all" respects what the user filtered down to. */
    const handleShowServiceHoursAssistant = (bookingIds: number[] | null, filter?: ActionCallbackProps['filter']) => {
        addModal({
            title: t('booking.list.modals.creditServiceHours.title'),
            content: (id) => (
                <BookingServiceHoursAssistant
                    eventId={eventId}
                    bookingIds={bookingIds}
                    filter={filter}
                    callback={() => {
                        removeModal(id);
                        asyncList.current?.reload();
                    }}
                />
            ),
            size: 70,
            show: true
        });
    };

    /** Opens an invoice PDF in a new browser tab. */
    const handleShowPDF = useCallback((invoice: Pick<UserInvoice, 'url'>) => {
        let popup = window.open(invoice.url, '_blank');
        if (popup != null) popup.focus();
    }, []);

    return (
        <DataTableContainer className="tw-overflow-hidden">
            <div className="tw-flex tw-flex-wrap tw-gap-4">
                <Button
                    className="tw-w-full tw-py-5 sm:tw-flex-1 sm:tw-basis-[260px]"
                    onPress={handleShowManualBookingForm}
                >
                    {t('booking.list.buttons.createManualBooking')}
                </Button>
                <Button
                    className="tw-w-full tw-py-5 sm:tw-flex-1 sm:tw-basis-[260px]"
                    flat
                    onPress={handleShowDocumentImport}
                >
                    {t('booking.list.buttons.documentImport')}
                </Button>
                <Link
                    className="tw-w-full sm:tw-flex-1 sm:tw-basis-[260px]"
                    href="/printview/[type]/[id]"
                    as={'/printview/event/' + eventId}
                    target="_blank"
                >
                    <Button className="tw-w-full tw-py-5">{t('booking.list.buttons.printView')}</Button>
                </Link>
            </div>
            <DataTable
                showStatistic
                className="tw-flex-1"
                asyncListRef={asyncList}
                columns={columns}
                filters={filters}
                tableProps={{
                    sticked: true,
                    bordered: true,
                    borderWeight: 'light',
                    compact: false,
                    containerCss: {},
                    selectionMode: 'multiple'
                }}
                actions={actions}
                actionsLoading={isLoading}
                fetchUrl={`booking/${eventId}/list`}
                fetchData={{ archive }}
                fetchMethod="GET"
                rowActions={(item) => (
                    <TableActionButton.Container justify="flex-end">
                        <TableActionButton
                            icon={faEnvelope}
                            tooltip={t('booking.list.rowActions.message')}
                            onPress={() => handleShowMessageComponent({ selection: [item.id] })}
                        />
                        <TableActionButton
                            icon={faUser}
                            tooltip={t('booking.list.rowActions.createMember')}
                            onPress={() => handleShowClubMemberAssistant([item.id])}
                        />
                        {item.form_data_id && (
                            <>
                                <TableActionButton
                                    icon={faDatabase}
                                    tooltip={t('booking.list.rowActions.formData')}
                                    onPress={() => handleShowFormData(item)}
                                />
                                <TableActionButton
                                    icon={faEdit}
                                    tooltip={t('booking.list.rowActions.editFormData')}
                                    onPress={() => handleShowEditBooking(item.form_data_id)}
                                />
                            </>
                        )}
                        {!archive && (
                            <>
                                <TableActionButton
                                    icon={item.read_at ? faEyeSlash : faEye}
                                    color={item.read_at ? 'default' : 'warning'}
                                    tooltip={
                                        item.read_at
                                            ? t('booking.list.rowActions.markUnread')
                                            : t('booking.list.rowActions.markRead')
                                    }
                                    onPress={() => handleToggleRead(item)}
                                />
                                <TableActionButton
                                    icon={faFileInvoice}
                                    tooltip={t('booking.list.rowActions.createInvoice')}
                                    onPress={() => handleGenerateInvoiceMultiple({ selection: [item.id] })}
                                />
                                {item.invoices?.length > 0 && (
                                    <TableActionButton
                                        icon={faBinoculars}
                                        tooltip={t('booking.list.rowActions.showLastInvoice')}
                                        onPress={() => handleShowPDF(head(item.invoices))}
                                    />
                                )}

                                {item.approval_status === 'pending' && (
                                    <>
                                        <TableActionButton
                                            icon={faCheck}
                                            color="success"
                                            tooltip={t('booking.list.rowActions.accept')}
                                            onPress={() => handleShowRequestResponseForm(item, 'accept')}
                                        />
                                        <TableActionButton
                                            icon={faTimes}
                                            color="error"
                                            tooltip={t('booking.list.rowActions.decline')}
                                            onPress={() => handleShowRequestResponseForm(item, 'decline')}
                                        />
                                    </>
                                )}
                                {item.is_waiting && !item.cancelled_at && item.approval_status !== 'pending' && (
                                    <TableActionButton
                                        icon={faHourglassEnd}
                                        color="warning"
                                        tooltip={t('booking.list.rowActions.moveUp')}
                                        onPress={() => handleMoveUp(item)}
                                    />
                                )}
                                {!item.cancelled_at ? (
                                    <TableActionButton
                                        icon={faBan}
                                        color="error"
                                        tooltip={t('booking.list.rowActions.cancel')}
                                        onPress={() => handleShowCancellationForm(item)}
                                    />
                                ) : (
                                    <TableActionButton
                                        icon={faUndo}
                                        color="error"
                                        tooltip={t('booking.list.rowActions.revokeCancellation')}
                                        onPress={() => handleRevokeCancellation(item)}
                                    />
                                )}
                                <TableActionButton
                                    icon={faTrashAlt}
                                    color="error"
                                    tooltip={t('booking.list.rowActions.delete')}
                                    onPress={() => handleShowDeleteBooking(item)}
                                />
                            </>
                        )}
                    </TableActionButton.Container>
                )}
            />
        </DataTableContainer>
    );
};

export default BookingList;
